// Web post-mortem boot marker (O2, plans/boot-health.md Step 22, #4027).
//
// Problem: web/PWA has NO post-mortem boot watchdog. All FreezeWatchdog
// machinery is native-gated, so a web boot that wedges and then dies (browser
// OOM kill, crashed tab, force-closed PWA) leaves zero telemetry — permanent
// silence for exactly the boots we most need to see (observability gaps
// O2/O3 in design-docs/boot-sequence.md).
//
// How it works — the web counterpart of the native FreezeWatchdog:
// - app.component.ts writes this marker to localStorage on every `bootStage`
//   transition (stage + timestamp). The first write of a boot happens early
//   (PreLogin), so a marker found at boot start always describes the
//   PREVIOUS boot.
// - a `pagehide` listener stamps `cleanExit` — the page is leaving on purpose
//   (tab close, navigation away, our own reload), so the next boot must not
//   count it as a wedge. A later stage transition clears the stamp again
//   (the page evidently kept running, e.g. a bfcache resume).
// - a `visibilitychange`→hidden listener (while the boot has not reached
//   Ready) stamps `hiddenDuringBoot` — the boot was backgrounded mid-flight.
//   This mirrors classifyBootFreeze's 'abandoned-background' downgrade
//   (#3797): a backgrounded boot that then dies is normal browser lifecycle,
//   not a wedge, and must not pollute the real-hang signal.
// - on the next boot, app.component.ts captures the previous marker BEFORE
//   the first transition overwrites it, and reports once the new boot reaches
//   Ready: a foreground death mid-boot surfaces as the `WebBootIncomplete`
//   silent problem (with the last stage reached); a backgrounded one is
//   downgraded to the low-signal `WebBootAbandonedBackground` key.
//
// More than one tab (#6181): localStorage is shared by every tab of the site,
// but there is only one marker. So each marker records which page load wrote
// it (`writerId`, a fresh id per page load), and a page holds a Web Lock
// named after that id from boot start until shortly after Ready (#6266: a
// tab may read a stale copy of localStorage, so the lock outlives the
// `ready` write; see releaseWebBootLockAfterReady). The reader asks which boot
// locks are held at the moment it reads the marker: a marker whose writer
// still held its lock was written by a page still alive in another tab,
// not by a boot that died, and is classified 'other-tab-booting' instead of
// being reported. The stamps follow the same owner rule: pagehide and the
// hidden stamp only touch a marker this page wrote, and a stage write only
// inherits hiddenDuringBoot from this page's own marker. Without Web Locks,
// or for a marker with no writerId (older app versions), behaviour is as
// before. Full story: design-docs/boot-sequence.md, O2.
//
// Storage rules: localStorage key `bootProgress`, wiped on signOut BY
// OMISSION from local-storage-hygiene.ts's survivor allow-list. Do NOT
// "helpfully" add the key to that allow-list: the marker is boot-scoped
// evidence, and surviving a signOut would let one account's aborted boot be
// reported inside the next account's session. The hygiene test pins this.
//
// Pure module — no Angular / Sentry imports. `evaluateWebBootMarker` takes
// the raw string so the decision logic runs under plain node in unit tests
// (same pattern as splash-watchdog.ts / boot-freeze-classifier.ts). The
// write helpers touch localStorage and can therefore throw (Safari private
// mode, full quota) — the caller guards them (see app.component.ts's
// once-latched guard), keeping this module Sentry-free.

import { DateTime } from 'luxon';

// the localStorage key. literal is cross-checked by web-boot-marker.test.ts
// and local-storage-hygiene.test.ts (wipe-by-omission expectation).
export const WEB_BOOT_MARKER_KEY = 'bootProgress';

// the Web Lock a booting page holds is named this prefix plus its page id
export const WEB_BOOT_LOCK_PREFIX = 'cs-web-boot:';

// makes the id of this page load. deliberately NOT getSessionId()'s tab id:
// that lives in sessionStorage, which a duplicated tab copies, so two live
// tabs could share it
function makePageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// the id of this page load, made once when the module loads
export const WEB_BOOT_PAGE_ID = makePageId();

export interface WebBootMarker {
  // the last bootStage value the previous boot reached (enum string form),
  // or BOOTSTRAP_FAILED_STAGE when Angular's bootstrapModule itself rejected
  stage: string;
  // ISO timestamp of that transition
  ts: string;
  // set by the pagehide listener: the page left on purpose
  cleanExit?: boolean;
  // set by the visibilitychange listener: backgrounded before Ready
  hiddenDuringBoot?: boolean;
  // set by recordBootstrapFailure (Step 23): the rejection text, so the next
  // boot's WebBootIncomplete report carries WHY the bootstrap died
  bootstrapError?: string;
  // the page load that wrote this marker (#6181). absent on markers written
  // by app versions before that change
  writerId?: string;
}

// the pseudo-stage recorded when bootstrapModule rejects (Step 23) — never a
// real BootStage value, so it always classifies as an incomplete boot
export const BOOTSTRAP_FAILED_STAGE = 'bootstrap-failed';

// classification of the previous boot's marker, mirroring the shape of
// classifyBootFreeze (report flag + category) so query/report code reads the
// same on both platforms
export type WebBootMarkerCategory =
  | 'no-marker' // nothing stored: fresh install, post-signout, first run on this build
  | 'corrupt' // unparseable/malformed marker — nothing trustworthy to report
  | 'completed' // previous boot reached Ready — healthy
  | 'clean-exit' // previous page exited on purpose mid-boot (tab close / navigation / reload)
  | 'other-tab-booting' // written by a page still alive in another tab: booting (#6181), or Ready within the lock's grace period (#6266) — nobody died
  | 'abandoned-background' // backgrounded mid-boot, then died — downgrade, not a wedge
  | 'incomplete'; // died mid-boot in the foreground — the real signal, REPORT

export interface WebBootMarkerDecision {
  // report as WebBootIncomplete (true only for category 'incomplete')
  report: boolean;
  // report as the low-signal WebBootAbandonedBackground instead (true only
  // for category 'abandoned-background')
  downgrade: boolean;
  category: WebBootMarkerCategory;
  // the stage the previous boot last reached (present when a marker parsed)
  lastStage?: string;
  // that transition's timestamp (present when a marker parsed)
  lastTs?: string;
  // the bootstrap rejection text, when the previous boot died in
  // bootstrapModule itself (Step 23)
  bootstrapError?: string;
}

// builds the all-false decision for the categories with nothing to report
function silentDecision(category: WebBootMarkerCategory): WebBootMarkerDecision {
  return { report: false, downgrade: false, category };
}

// Pure classifier for the PREVIOUS boot's marker, sampled at boot start.
// `readyStageValue` is the string form of BootStage.Ready — passed in so this
// module does not import app.component's private enum.
// `liveBootPageIds` is the set of page ids whose boot lock was held when the
// marker was read (queryHeldWebBootPageIdsAsync), or null when that could not
// be asked — null keeps the classification exactly as it was before #6181.
export function evaluateWebBootMarker(raw: string | null, readyStageValue: string, liveBootPageIds: Set<string> | null = null): WebBootMarkerDecision {
  if (!raw) return silentDecision('no-marker');

  let marker: Partial<WebBootMarker>;
  try {
    marker = JSON.parse(raw) as Partial<WebBootMarker>;
  } catch {
    // expected: SyntaxError from JSON.parse on a tampered or half-written
    // marker. There is nothing trustworthy to report from a corrupt marker,
    // and the current boot's own transitions overwrite it immediately
    // (self-healing), so classify silently instead of breaking a boot over
    // its own post-mortem evidence.
    return silentDecision('corrupt');
  }
  if (typeof marker?.stage !== 'string' || typeof marker.ts !== 'string') return silentDecision('corrupt');

  // if the previous boot reached Ready, it completed — nothing to report
  if (marker.stage === readyStageValue) {
    return { ...silentDecision('completed'), lastStage: marker.stage, lastTs: marker.ts };
  }
  // if the page that wrote the marker was still booting when it was read, the
  // boot is running in another tab, not dead (#6181). checked before the
  // stamps: those describe a page that is gone, and this one is not
  if (typeof marker.writerId === 'string' && marker.writerId !== WEB_BOOT_PAGE_ID && liveBootPageIds?.has(marker.writerId)) {
    return { ...silentDecision('other-tab-booting'), lastStage: marker.stage, lastTs: marker.ts };
  }
  // if the page announced its exit (pagehide), the death was deliberate
  if (marker.cleanExit === true) {
    return { ...silentDecision('clean-exit'), lastStage: marker.stage, lastTs: marker.ts };
  }
  // if the boot was backgrounded before Ready, downgrade — browser lifecycle
  // kills of hidden pages are normal, not wedges (mirrors #3797)
  if (marker.hiddenDuringBoot === true) {
    return { report: false, downgrade: true, category: 'abandoned-background', lastStage: marker.stage, lastTs: marker.ts };
  }
  // foreground death mid-boot: the genuine post-mortem signal. carries the
  // bootstrap rejection text when the death was bootstrapModule itself
  const decision: WebBootMarkerDecision = { report: true, downgrade: false, category: 'incomplete', lastStage: marker.stage, lastTs: marker.ts };
  if (typeof marker.bootstrapError === 'string') decision.bootstrapError = marker.bootstrapError;
  return decision;
}

// decides whether a `storage` event (another tab's write reaching this tab)
// proves the note this page read at boot start was a stale copy (#6266): it
// carries a different note from the SAME writer that was written BEFORE this
// page read, so that write had not reached this tab when it read. Evidence for
// the report only — the classification does not use it.
export function isStaleWebBootMarkerRead(capturedRaw: string | null, readAtIso: string, event: { key: string | null; newValue: string | null }): boolean {
  if (event.key !== WEB_BOOT_MARKER_KEY || !capturedRaw || !event.newValue) return false;
  const captured = parseMarkerOrNull(capturedRaw);
  const newer = parseMarkerOrNull(event.newValue);
  if (typeof captured?.writerId !== 'string' || newer?.writerId !== captured.writerId) return false;
  const capturedAt = DateTime.fromISO(captured.ts);
  const newerAt = DateTime.fromISO(newer.ts);
  const readAt = DateTime.fromISO(readAtIso);
  if (!capturedAt.isValid || !newerAt.isValid || !readAt.isValid) return false;
  // newer than what we read, yet written before we read: an older note from
  // the same writer (a queued event for a write we had already seen past)
  // proves nothing
  return newerAt > capturedAt && newerAt <= readAt;
}

// reads the current raw marker (for the boot-start capture). kept trivial so
// the caller owns the null-vs-string decision; parsing happens in evaluate.
export function readWebBootMarkerRaw(): string | null {
  return localStorage.getItem(WEB_BOOT_MARKER_KEY);
}

// removes the stored marker. MUST be called right after the boot-start
// capture: recordWebBootStage inherits the sticky hiddenDuringBoot flag from
// whatever marker is stored, and on a new boot's FIRST transition that would
// be the PREVIOUS boot's marker. A backgrounded-but-completed boot leaves
// {stage:'ready', hiddenDuringBoot:true}; without this clear, the next boot
// would start contaminated and a genuine foreground wedge in it would be
// downgraded to abandoned-background — suppressing exactly the
// WebBootIncomplete signal this module exists for.
export function clearWebBootMarker(): void {
  localStorage.removeItem(WEB_BOOT_MARKER_KEY);
}

// records "Angular bootstrapModule itself rejected" (Step 23). This DELIBERATELY
// overwrites whatever marker is stored: when bootstrap fails, the boot never
// reached the capture-and-clear step, so the stored marker is the previous
// boot's — but the bootstrap failure is the fresher, more urgent evidence, and
// only one post-mortem can be reported per key. The next SUCCESSFUL boot
// reports it as WebBootIncomplete with last_stage 'bootstrap-failed' and the
// rejection text. NOTE for callers: localStorage can throw — guard at the site.
export function recordBootstrapFailure(errorText: string, nowIso: string): void {
  const marker: WebBootMarker = {
    stage: BOOTSTRAP_FAILED_STAGE,
    ts: nowIso,
    bootstrapError: errorText,
    writerId: WEB_BOOT_PAGE_ID,
  };
  localStorage.setItem(WEB_BOOT_MARKER_KEY, JSON.stringify(marker));
}

// records a bootStage transition. preserves the sticky hiddenDuringBoot
// annotation within the same boot window and clears a stale cleanExit stamp —
// if a transition runs after pagehide stamped the exit, the page evidently
// kept running (bfcache resume), so the exit never happened.
// NOTE for callers: localStorage access can throw — guard at the call site.
export function recordWebBootStage(stage: string, nowIso: string): void {
  const previous = parseCurrentMarkerOrNull();
  const marker: WebBootMarker = { stage, ts: nowIso, writerId: WEB_BOOT_PAGE_ID };
  // if this page's boot was already backgrounded once, keep that annotation
  // sticky. only from this page's own marker: another tab's hidden stamp says
  // nothing about this boot (#6181)
  if (previous?.writerId === WEB_BOOT_PAGE_ID && previous.hiddenDuringBoot) marker.hiddenDuringBoot = true;
  localStorage.setItem(WEB_BOOT_MARKER_KEY, JSON.stringify(marker));
}

// stamps "the boot was backgrounded before Ready" onto the current marker.
// no-op when no marker exists yet (nothing to annotate).
// DELIBERATE: the stamp is one-way within a boot window — returning to the
// foreground does not clear it, so a boot that bounced to background once and
// later wedged in the foreground still classifies as abandoned-background.
// This mirrors the native #3797 model ("any backgrounding downgrades"): the
// browser may throttle or freeze a background page's timers and JS, so a
// wedge in a boot that was EVER backgrounded cannot be attributed to the app
// with confidence. We trade a slice of genuine wedges for a clean
// WebBootIncomplete signal.
export function markWebBootHiddenDuringBoot(): void {
  annotateCurrentMarker(marker => { marker.hiddenDuringBoot = true; });
}

// stamps "the page is leaving on purpose" onto the current marker (pagehide).
// no-op when no marker exists yet.
export function markWebBootCleanExit(): void {
  annotateCurrentMarker(marker => { marker.cleanExit = true; });
}

// reads and parses the current marker, treating corrupt JSON as absent —
// expected: SyntaxError from JSON.parse on a tampered marker (same
// self-healing rationale as evaluateWebBootMarker's corrupt branch)
function parseCurrentMarkerOrNull(): WebBootMarker | null {
  return parseMarkerOrNull(localStorage.getItem(WEB_BOOT_MARKER_KEY));
}

// parses one raw marker, treating a missing or corrupt one as absent
function parseMarkerOrNull(raw: string | null): WebBootMarker | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<WebBootMarker>;
    if (typeof parsed?.stage !== 'string' || typeof parsed.ts !== 'string') return null;
    return parsed as WebBootMarker;
  } catch {
    // a corrupt current marker is treated as absent: this boot's next stage write replaces it
    return null;
  }
}

// applies one mutation to the stored marker, writing it back in place. only
// when this page wrote it: the stored marker may belong to a boot running in
// another tab, and a stamp from this page would misdescribe it (#6181) — an
// old tab closing would otherwise mark another tab's boot as a clean exit
function annotateCurrentMarker(mutate: (marker: WebBootMarker) => void): void {
  const marker = parseCurrentMarkerOrNull();
  if (!marker || marker.writerId !== WEB_BOOT_PAGE_ID) return;
  mutate(marker);
  localStorage.setItem(WEB_BOOT_MARKER_KEY, JSON.stringify(marker));
}

// takes this page's boot lock (#6181) and returns the function that releases
// it, or null when the browser has no Web Locks. The lock is what tells
// another tab "this page is alive"; the browser releases it on its own when
// the page dies. The caller releases it shortly after Ready
// (releaseWebBootLockAfterReady), so a finished page soon becomes eligible
// for the back-forward cache (Chrome refuses that cache to a page holding a
// Web Lock).
// A browser that refuses the lock (an opaque origin, blocked storage) hands
// the error to `onRefused` when it rejects later; a synchronous throw goes to
// the caller. Either way the caller reports it — this module stays Sentry-free.
export function acquireWebBootLock(onRefused: (err: unknown) => void): (() => void) | null {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return null;
  let release: () => void = () => {};
  const held = new Promise<void>(resolve => { release = resolve; });
  locks.request(WEB_BOOT_LOCK_PREFIX + WEB_BOOT_PAGE_ID, () => held).catch(onRefused);
  return release;
}

// how long a page keeps its boot lock after reaching Ready (#6266)
export const WEB_BOOT_LOCK_GRACE_AFTER_READY_MS = 10_000;

// releases this page's boot lock a while after the boot reached Ready, not at
// once (#6266). A tab that starts just after this one may read a stale copy of
// localStorage, in which this page's note still shows a mid-boot stage: when
// an Update tap reloads every tab, the background tabs read the foreground
// tab's note 0.3 s after it said ready and still saw it mid-boot. Lock state
// is the same in every tab, so a lock still held keeps that note from being
// reported as a dead boot. The cost: a page left within the grace period
// misses the back-forward cache once (Chrome refuses it to a page holding a
// Web Lock). `schedule` is setTimeout in the app and a hand-driven timer in
// tests
export function releaseWebBootLockAfterReady(release: () => void, schedule: (run: () => void, ms: number) => unknown): void {
  schedule(release, WEB_BOOT_LOCK_GRACE_AFTER_READY_MS);
}

// asks which pages hold a boot lock right now, and returns their page ids
// (#6181). null when the browser has no Web Locks: evaluateWebBootMarker then
// classifies exactly as before #6181. A failed query rejects, for the caller
// to report and fall back the same way.
export async function queryHeldWebBootPageIdsAsync(): Promise<Set<string> | null> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return null;
  const snapshot = await locks.query();
  const ids = new Set<string>();
  for (const lock of snapshot.held ?? []) {
    if (typeof lock.name === 'string' && lock.name.startsWith(WEB_BOOT_LOCK_PREFIX)) ids.add(lock.name.slice(WEB_BOOT_LOCK_PREFIX.length));
  }
  return ids;
}
